import SignupCompletionGate from "@/features/auth/SignupCompletionGate";
import React from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { AIAcknowledgementHost } from "@/components/ai/AIAcknowledgementHost";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { AppDataProvider } from "./context/AppDataContext";
import { UserPreferencesProvider } from "./context/UserPreferencesContext";
import { TimerProvider } from "./context/TimerContext";
import { FocusModeProvider } from "./context/FocusModeContext";
import { ActivityTrackerProvider } from "./context/ActivityTrackerContext";
import { AchievementProvider } from "./context/AchievementContext";
import { UserProgressProvider } from "./context/UserProgressContext";
import { AIActivityProvider } from "./context/AIActivityContext";
import { ProgressiveProfileProvider } from "./context/ProgressiveProfileContext";
import { ProgressivePromptOrchestrator } from "./components/progressive/ProgressivePrompts";
import { MotionConfig } from "framer-motion";
import { posthog } from "./lib/posthog";
import { initVisitorAttribution } from "./lib/analytics/analytics";
import { initWebVitalsTracking } from "./lib/analytics/webVitals";
import { useLocation, useParams } from "react-router-dom";

import { GlobalErrorBoundary, GlobalErrorFallback } from "@/components/GlobalErrorBoundary";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import { RouteErrorBoundary } from "@/components/ui/RouteErrorBoundary";
import ProtectedRoute from "@/components/ui/ProtectedRoute";
import NovariLoadingScreen from "@/components/ui/NovariLoadingScreen";
import KidsAlertHost from "@/components/kids/KidsAlertHost";
import UsageLimitModal from "@/components/UsageLimitModal";
import KidsLoadingScreen from "@/components/kids/KidsLoadingScreen";
import { lazyWithRetry } from "./lib/lazyWithRetry";
import { AppStatusModal } from "@/components/AppStatusModal";
import { TesterProvider } from "@/features/testing/TesterContext";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import { useAuth } from "@/context/AuthContext";
import { gradeLevelStorage } from "@/lib/localStorage";
import { isAppHost } from "@/lib/hostRouting";
import OfflineBanner from "@/components/OfflineBanner";
import ScrollExperience from "@/components/ScrollExperience";
import { useBrandFavicon } from "@/components/ui/BrandIcon";

// Lazy-loaded features
const Landing = lazyWithRetry(() => import("@/features/auth/Landing"));
const Login = lazyWithRetry(() => import("@/features/auth/Login"));
const Signup = lazyWithRetry(() => import("@/features/auth/Signup"));
const ForgotPassword = lazyWithRetry(() => import("@/features/auth/ForgotPassword"));
const UpdatePassword = lazyWithRetry(() => import("@/features/auth/UpdatePassword"));
const AuthCallback = lazyWithRetry(() => import("@/features/auth/AuthCallback"));
const Onboarding = lazyWithRetry(() => import("@/features/onboarding/Onboarding"));
const Dashboard = lazyWithRetry(() => import("@/features/analytics/Dashboard"));
const ChatLearning = lazyWithRetry(() => import("@/features/chat/ChatLearning"));
const ChatHistory = lazyWithRetry(() => import("@/features/chat/ChatHistory"));
const Documents = lazyWithRetry(() => import("@/features/docs/Documents"));
const DocumentPage = lazyWithRetry(() => import("@/features/docs/DocumentPage"));
const SmartReader = lazyWithRetry(() => import("@/features/docs/SmartReader"));
const SmartTextbookView = lazyWithRetry(() => import("@/features/docs/SmartTextbookView"));
const FlashcardsPage = lazyWithRetry(() => import("@/features/docs/FlashcardsPage"));
const StudyHub = lazyWithRetry(() => import("@/features/study/StudyHub"));
const StudyWorkspace = lazyWithRetry(() => import("@/features/docs/StudyWorkspace"));
const FlashcardStudy = lazyWithRetry(() => import("@/features/docs/FlashcardStudy"));
const QuizStudy = lazyWithRetry(() => import("@/features/docs/QuizStudy"));
const QuizStudyPage = lazyWithRetry(() => import("@/features/docs/QuizStudyPage"));
const AcademicWorkspace = lazyWithRetry(() => import("@/features/curriculum/AcademicWorkspace"));
const CurriculumPage = lazyWithRetry(() => import("@/features/curriculum/CurriculumPage"));
const TopicHubPage = lazyWithRetry(() => import("@/features/curriculum/TopicHubPage"));
const QuizSession = lazyWithRetry(() => import("@/features/docs/quiz/QuizSession"));
const QuestionSetsPage = lazyWithRetry(() => import("@/features/questions/QuestionSetsPage"));
const QuestionSetPage = lazyWithRetry(() => import("@/features/questions/QuestionSetPage"));
const PaperAttemptPage = lazyWithRetry(() => import("@/features/questions/paper/PaperAttemptPage"));
// Remount per session so a new attempt ("Try again") never inherits the
// previous attempt's answers, drafts or results.
// DEV-only quiz layout harness (mocked API, no sign-in). Dead code in builds.
const QuizHarness = import.meta.env.DEV ? lazyWithRetry(() => import("@/dev/QuizHarness")) : null;
function KeyedQuizSession() {
  const { sessionId } = useParams<{ sessionId: string }>();
  return <QuizSession key={sessionId} />;
}
const LessonWorkspace = lazyWithRetry(() => import("@/features/docs/lesson-workspace/LessonWorkspace"));
const SummariesPage = lazyWithRetry(() => import("@/features/docs/SummariesPage"));
const SummaryView = lazyWithRetry(() => import("@/features/docs/SummaryView"));
const TemplateSummarize = lazyWithRetry(() => import("@/features/docs/TemplateSummarize"));
const Settings = lazyWithRetry(() => import("@/features/auth/Settings"));
const KidsProfile = lazyWithRetry(() => import("@/pages/KidsProfile"));
const FAQ = lazyWithRetry(() => import("@/features/support/FAQ"));
const ContactPage = lazyWithRetry(() => import("@/features/support/ContactPage").then(m => ({ default: m.ContactPage })));
const IssuesPage = lazyWithRetry(() => import("@/features/support/IssuesPage"));
const FeedbackBoard = lazyWithRetry(() => import("@/features/support/FeedbackBoard"));
const PrivacyPolicy = lazyWithRetry(() => import("@/features/support/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("@/features/support/TermsOfService"));
const CookiePolicy = lazyWithRetry(() => import("@/features/support/CookiePolicy"));
const MainLayout = lazyWithRetry(() => import("@/layouts/MainLayout"));
const ProgressWorkspace = lazyWithRetry(() => import("@/features/analytics/ProgressWorkspace"));
const AchievementsPage = lazyWithRetry(() => import("@/features/analytics/AchievementsPage"));
const WhatsNew = lazyWithRetry(() => import("@/features/support/WhatsNew"));
const PlannerPage = lazyWithRetry(() => import("@/features/planner/PlannerPage"));
const MaintenancePage = lazyWithRetry(() => import("@/pages/MaintenancePage"));
const VerifyPayment = lazyWithRetry(() => import("@/components/VerifyPayment"));
const Paywall = lazyWithRetry(() => import("@/components/Paywall"));
const Pricing = lazyWithRetry(() => import("@/pages/Pricing"));
const SharedResourceView = lazyWithRetry(() => import("@/features/sharing/SharedResourceView"));
const ClassroomPage = lazyWithRetry(() => import("./features/docs/ClassroomPage"));
const GroupsPage = lazyWithRetry(() => import("@/pages/GroupsPage"));
const NotFound = lazyWithRetry(() => import("@/components/ui/NotFound"));
const ArticlesIndexView = lazyWithRetry(() => import("@/features/articles/ArticlesIndexView"));
const CategoryHubView = lazyWithRetry(() => import("@/features/articles/CategoryHubView"));
const ArticleView = lazyWithRetry(() => import("@/features/articles/ArticleView"));


const MAINTENANCE_MODE = import.meta.env.VITE_MAINTENANCE_MODE === "true";

function HomeRoute() {
  const { session, isLoading, authReady } = useAuth();

  // Return null while auth resolves — the Suspense fallback already showed a
  // loader while chunks were downloading. A second full-screen LoadingScreen
  // here creates the "double loading screen" effect.
  if (isLoading || !authReady) {
    return null;
  }

  if (session) {
    return <Navigate to="/dashboard" replace />;
  }

  // Single-domain: novaristudy.site serves both the marketing landing page
  // and the app — see hostRouting.ts. isAppHost() always returns false, so
  // this only ever renders Landing for an unauthenticated visitor.
  if (isAppHost()) {
    return <Navigate to="/login" replace />;
  }

  return <Landing />;
}

function PostHogPageviewTracker() {
  const location = useLocation();

  React.useEffect(() => {
    // No preference gate needed here — posthog-js itself no-ops capture()
    // calls while opted out (see lib/posthog.ts's opt_out_capturing_by_default),
    // and CookieConsentBanner/Settings are the only things that flip that.
    posthog?.capture('$pageview', {
      $current_url: window.location.href,
      $pathname: location.pathname,
    });
  }, [location]);

  return null;
}

function GlobalAnalytics() {
  // Runs once for every visitor, authenticated or not — captures UTM/referrer
  // attribution before signup and starts Web Vitals collection app-wide.
  // Both route through posthog.capture(), which already respects consent
  // (see lib/posthog.ts) — no separate gating needed here.
  React.useEffect(() => {
    initVisitorAttribution();
    initWebVitalsTracking();
  }, []);

  return (
    <>
      <PostHogPageviewTracker />
    </>
  );
}

const router = createBrowserRouter(
  [
    {
      element: (
        <AIActivityProvider>
          <ScrollExperience />
          <GlobalAnalytics />
          <ProgressivePromptOrchestrator />
          <CookieConsentBanner />
          <Outlet />
        </AIActivityProvider>
      ),
      errorElement: <GlobalErrorFallback />,
      children: [
        { path: "/login", element: <Login /> },
        { path: "/login/kids", element: <Login kids /> },
        { path: "/signup", element: <Signup /> },
        { path: "/forgot-password", element: <ForgotPassword /> },
        { path: "/update-password", element: <UpdatePassword /> },
        { path: "/auth/callback", element: <AuthCallback /> },
        { path: "/complete-signup", element: <SignupCompletionGate showForm><Navigate to="/dashboard" replace /></SignupCompletionGate> },
        { path: "/onboarding", element: <ProtectedRoute><RouteErrorBoundary><Onboarding /></RouteErrorBoundary></ProtectedRoute> },
        { path: "/privacy", element: <PrivacyPolicy /> },
        { path: "/terms", element: <TermsOfService /> },
        { path: "/cookies", element: <CookiePolicy /> },
        { path: "/help", element: <RouteErrorBoundary><FAQ /></RouteErrorBoundary> },
        { path: "/articles", element: <ArticlesIndexView /> },
        { path: "/articles/topic/:category", element: <CategoryHubView /> },
        { path: "/articles/:slug", element: <ArticleView /> },
        { path: "/s/:shortCode", element: <SharedResourceView /> },
        { path: "/", element: <HomeRoute /> },
        { path: "/landing", element: <Navigate to="/" replace /> },
        {
          element: <MainLayout />,
          errorElement: <GlobalErrorFallback />,
          children: [
            { path: "/dashboard", element: <ProtectedRoute><RouteErrorBoundary><Dashboard /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/documents", element: <ProtectedRoute><RouteErrorBoundary><Documents /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/documents/:id", element: <ProtectedRoute><RouteErrorBoundary><DocumentPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/documents/:id/read", element: <ProtectedRoute><RouteErrorBoundary><SmartTextbookView /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/documents/:id/reader", element: <ProtectedRoute><RouteErrorBoundary><SmartReader /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/classroom", element: <ProtectedRoute><RouteErrorBoundary><ClassroomPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/groups", element: <ProtectedRoute><RouteErrorBoundary><GroupsPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/groups/:id", element: <ProtectedRoute><RouteErrorBoundary><GroupsPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/flashcards", element: <ProtectedRoute><RouteErrorBoundary><FlashcardsPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/summaries", element: <ProtectedRoute><RouteErrorBoundary><SummariesPage /></RouteErrorBoundary></ProtectedRoute> },
            // Progress is the single learner-facing pillar. Mastery is the
            // default view; detailed analytics is addressable at
            // /progress?view=analytics so old links remain deep-linkable without
            // creating a second navigation destination.
            { path: "/progress", element: <ProtectedRoute><RouteErrorBoundary><ProgressWorkspace /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/analytics", element: <Navigate to="/progress?view=analytics" replace /> },
            // /mastery kept as a redirect so old links/bookmarks still resolve.
            { path: "/mastery", element: <Navigate to="/progress" replace /> },
            { path: "/achievements", element: <ProtectedRoute><RouteErrorBoundary><AchievementsPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/chat", element: <ProtectedRoute><RouteErrorBoundary><ChatLearning /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/chat/history", element: <ProtectedRoute><RouteErrorBoundary><ChatHistory /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/contact", element: <ProtectedRoute><RouteErrorBoundary><ContactPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/issues", element: <ProtectedRoute><RouteErrorBoundary><IssuesPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/feedback", element: <ProtectedRoute><RouteErrorBoundary><FeedbackBoard /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/settings", element: <ProtectedRoute><RouteErrorBoundary><Settings /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/settings/kids", element: <ProtectedRoute><RouteErrorBoundary><Settings /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/profile/kids", element: <ProtectedRoute><RouteErrorBoundary><KidsProfile /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/whats-new", element: <ProtectedRoute><RouteErrorBoundary><WhatsNew /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/planner", element: <ProtectedRoute><RouteErrorBoundary><PlannerPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/curriculum/workspace", element: <ProtectedRoute><RouteErrorBoundary><AcademicWorkspace /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/curriculum", element: <ProtectedRoute><RouteErrorBoundary><CurriculumPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/curriculum/specs", element: <ProtectedRoute><RouteErrorBoundary><CurriculumPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/curriculum/spec/:specId", element: <ProtectedRoute><RouteErrorBoundary><CurriculumPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/curriculum/topic/:topicId", element: <ProtectedRoute><RouteErrorBoundary><TopicHubPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/curriculum/:key", element: <ProtectedRoute><RouteErrorBoundary><CurriculumPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/quiz-study", element: <ProtectedRoute><RouteErrorBoundary><QuizStudyPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/questions", element: <ProtectedRoute><RouteErrorBoundary><QuestionSetsPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/questions/:id", element: <ProtectedRoute><RouteErrorBoundary><QuestionSetPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/papers/:attemptId", element: <ProtectedRoute><RouteErrorBoundary><PaperAttemptPage /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/quiz/session/:sessionId", element: <ProtectedRoute><RouteErrorBoundary><KeyedQuizSession /></RouteErrorBoundary></ProtectedRoute> },
            ...(QuizHarness ? [{ path: "/quiz/session-harness", element: <QuizHarness /> }] : []),
            // Study pillar hub. The coach is the default action; Flashcards /
            // Quizzes / Summaries remain reachable as modes rather than nav peers.
            { path: "/study", element: <ProtectedRoute><RouteErrorBoundary><StudyHub /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/study/:studySetId", element: <ProtectedRoute><RouteErrorBoundary><StudyWorkspace /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/study/:studySetId/flashcards", element: <ProtectedRoute><RouteErrorBoundary><FlashcardStudy /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/study/:studySetId/quiz", element: <ProtectedRoute><RouteErrorBoundary><QuizStudy /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/study/:studySetId/summary", element: <ProtectedRoute><RouteErrorBoundary><SummaryView /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/lesson/:studySetId", element: <ProtectedRoute><RouteErrorBoundary><LessonWorkspace /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/templates/summarize", element: <ProtectedRoute><RouteErrorBoundary><TemplateSummarize /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/verify-payment", element: <ProtectedRoute><RouteErrorBoundary><VerifyPayment /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/pricing", element: <ProtectedRoute><RouteErrorBoundary><Pricing /></RouteErrorBoundary></ProtectedRoute> },
            { path: "/paywall", element: <Navigate to="/pricing" replace /> },
          ],
        },
        { path: "*", element: <NotFound /> },
      ]
    }
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);

function Providers({ children, queryClient }: { children: React.ReactNode; queryClient: QueryClient }) {
  useBrandFavicon();
  return (
    <GlobalErrorBoundary>
      {/* Accessibility (reduced motion): honour the OS "reduce motion" setting
          for every framer-motion animation app-wide — transform/scale/layout
          animations are dropped, opacity fades are kept. One switch instead of
          per-component guards. */}
      <MotionConfig reducedMotion="user">
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
          <AuthProvider>
            <AppDataProvider>
              <UserPreferencesProvider>
                <TesterProvider>
                <UserProgressProvider>
                  <ProgressiveProfileProvider>
                  <ActivityTrackerProvider>
                    <AchievementProvider>
                      <TimerProvider>
                        <FocusModeProvider>
                          {children}
                        </FocusModeProvider>
                      </TimerProvider>
                    </AchievementProvider>
                  </ActivityTrackerProvider>
                  </ProgressiveProfileProvider>
                </UserProgressProvider>
                </TesterProvider>
              </UserPreferencesProvider>
            </AppDataProvider>
          </AuthProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </MotionConfig>
    </GlobalErrorBoundary>
  );
}

function App() {
  React.useEffect(() => {
    window.sessionStorage.removeItem('last-retry-status');
  }, []);

  if (MAINTENANCE_MODE) {
    return (
      <GlobalErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ErrorBoundary>
              <MaintenancePage />
            </ErrorBoundary>
          </AuthProvider>
        </QueryClientProvider>
      </GlobalErrorBoundary>
    );
  }

  const isKids = gradeLevelStorage.isKids();

  return (
    <Providers queryClient={queryClient}>
      <React.Suspense fallback={isKids ? <KidsLoadingScreen /> : <NovariLoadingScreen timeout={30000} />}>
        <RouterProvider router={router} />
      </React.Suspense>
      <Sonner />
      <KidsAlertHost />
      <UsageLimitModal onUpgrade={() => void router.navigate("/pricing")} />
      <AIAcknowledgementHost />
      <OfflineBanner />
      {/* Connectivity estimates must not lock students out of loaded material.
          OfflineBanner reports connectivity; each failed action owns its retry. */}
      <AppStatusModal />
    </Providers>
  );
}

export default App;
